import { createContext, useContext, useId, type AriaAttributes, type ReactNode } from 'react';
import { Controller, type Control, type ControllerRenderProps, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form';
import { cn } from './cn';

/** What a control inside a <Field> needs to be labelled and described. */
export interface FieldControlProps extends Pick<AriaAttributes, 'aria-describedby' | 'aria-invalid' | 'aria-required'> {
  id?: string;
}

const FieldContext = createContext<(FieldControlProps & { labelId: string }) | null>(null);

/** Id of the surrounding <Field>'s label, for group controls a <label for> cannot target (radio groups). */
export function useFieldLabelId(): string | undefined {
  return useContext(FieldContext)?.labelId;
}

/**
 * Props a kit control spreads onto its focusable element so the surrounding <Field>'s label,
 * hint and error apply to it. Explicit props win over the field's.
 */
export function useFieldControl(own: FieldControlProps = {}): FieldControlProps {
  const field = useContext(FieldContext);
  if (!field) return own;
  return {
    id: own.id ?? field.id,
    'aria-describedby': [field['aria-describedby'], own['aria-describedby']].filter(Boolean).join(' ') || undefined,
    'aria-invalid': own['aria-invalid'] ?? field['aria-invalid'],
    'aria-required': own['aria-required'] ?? field['aria-required'],
  };
}

export interface FieldProps {
  label: ReactNode;
  /** Help under the control. */
  hint?: ReactNode;
  /** Validation message; marks the control invalid. */
  error?: ReactNode;
  required?: boolean;
  /** Content at the right end of the label row (e.g. a "Calculate" link). */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Label + control + hint/error, wired for assistive tech: the label targets the control, the hint
 * and error describe it. Works with every kit control (Input, Select, Combobox, DatePicker...).
 */
export function Field({ label, hint, error, required, aside, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const control = {
    id,
    labelId: `${id}-label`,
    'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-required': required || undefined,
  };
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        {/* the required mark is a pseudo-element, so it stays out of the accessible name */}
        <label id={`${id}-label`} htmlFor={id} className={cn('text-sm font-bold text-ink', required && "after:ml-0.5 after:text-danger-ink after:content-['*']")}>
          {label}
        </label>
        {aside}
      </div>
      <FieldContext.Provider value={control}>{children}</FieldContext.Provider>
      {error ? (
        <p id={errorId} className="m-0 text-xs text-danger-ink">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="m-0 text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface FormFieldProps<V extends FieldValues, N extends FieldPath<V>> extends Omit<FieldProps, 'children' | 'error'> {
  control: Control<V>;
  name: N;
  rules?: RegisterOptions<V, N>;
  /** Renders the control from react-hook-form's field binding (`value`, `onChange`, `onBlur`, `ref`). */
  render(field: ControllerRenderProps<V, N>): ReactNode;
}

/**
 * A <Field> bound to react-hook-form: validation `rules` run on submit and the error shows under
 * the control. `required` is inferred from `rules.required`.
 *
 *   <FormField control={form.control} name="reason" label="Reason" rules={{ required: 'Choose a reason' }}
 *     render={(f) => <Select options={REASONS} value={f.value} onValueChange={f.onChange} />} />
 */
export function FormField<V extends FieldValues, N extends FieldPath<V>>({ control, name, rules, render, required, ...field }: FormFieldProps<V, N>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: binding, fieldState }) => (
        <Field {...field} required={required ?? !!rules?.required} error={fieldState.error?.message}>
          {render(binding)}
        </Field>
      )}
    />
  );
}
